import { Editor, Element, Transforms } from 'slate';

import { findNodeByElementId } from '../../shared-transforms';
import {
  CARD_LOGIC_CELL_ELEMENT_TYPE,
  CARD_LOGIC_ELEMENT_TYPE,
  CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE,
  generateSingleCardLogicCell,
  generateSingleCardLogicNode,
  generateSingleColumnLogicCell,
} from './constants';

/* eslint-disable no-plusplus */
function addNewCardLogic(editor) {
  if (!editor) {
    // eslint-disable-next-line no-console
    console.error('[CardLogic] Unable to find editor, did you forgot to register editor');
    return;
  }
  // Prevent add card logic inside card logic
  if (editor.selection) {
    const [foundNode] = Editor.nodes(editor, {
      at: editor.selection,
      match: (node: any) => node?.type === CARD_LOGIC_ELEMENT_TYPE,
    });

    if (foundNode) return;
  }

  Transforms.insertNodes(editor, generateSingleCardLogicNode());
}

function setNodesByElementId({ editor, elementId, value }) {
  Transforms.setNodes(editor, value, {
    at: [],
    match: (node: any) => {
      return node.id === elementId;
    },
  });
}

interface InsertCardByElementIdParams {
  editor: any;
  elementId: string;
  insertLocation: 'up' | 'down';
}

function insertCardByElementId({
  editor,
  elementId,
  insertLocation = 'down',
}: InsertCardByElementIdParams) {
  const cellToInsert = generateSingleCardLogicCell();
  try {
    const [, path] = findNodeByElementId({ editor, elementId });
    const [colIndex, cellIndex, rowIndex] = path;
    Transforms.insertNodes(editor, cellToInsert, {
      at: [colIndex, cellIndex, insertLocation === 'up' ? rowIndex : rowIndex + 1],
    });
  } catch (ex) {
    // Weird issue of slate which throw random error
  }
}

function deleteCardByElementId({ editor, elementId }) {
  const [, path] = findNodeByElementId({ editor, elementId });
  Transforms.delete(editor, { at: path });
}

function insertColummByElementId({ editor, elementId, insertLocation = 'right' }) {
  const [, path] = findNodeByElementId({ editor, elementId });
  const [, colPath] = Editor.parent(editor, path);
  const [cellIndex, rowIndex] = colPath;

  const colToInsert = generateSingleColumnLogicCell();

  Transforms.insertNodes(editor, colToInsert, {
    at: [cellIndex, insertLocation === 'up' ? rowIndex : rowIndex + 1],
  });
}

function removeColumnByElementId({ editor, elementId }) {
  const [, path] = findNodeByElementId({ editor, elementId });
  const [, colPath] = Editor.parent(editor, path);

  Transforms.deselect(editor);
  Transforms.delete(editor, { at: colPath });
}

function retrieveTableInfoByElementId({ editor, elementId }) {
  let isInCardLogic = false;
  let headerCellNodeElementId = '';
  let cellNodeElementId = '';
  let containerNode;
  let columnCount = 0;
  let colInfo = [];

  if (editor?.selection) {
    const nodeEntries = Editor.nodes(editor, {
      at: editor.selection,
    });

    for (const [node] of nodeEntries) {
      if (Element.isElement(node)) {
        switch (node?.type) {
          case CARD_LOGIC_ELEMENT_TYPE: {
            isInCardLogic = elementId === node.id;
            containerNode = node;
            break;
          }
          case CARD_LOGIC_CELL_ELEMENT_TYPE: {
            cellNodeElementId = node.id as string;
            break;
          }
          case CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE: {
            headerCellNodeElementId = node.id as string;
            break;
          }
        }
      }
    }
  }

  if (containerNode && isInCardLogic) {
    columnCount = containerNode.children.length;
    colInfo = containerNode.children.map((col) => {
      const [, ...rows] = col.children; // let's just ignore the header col at [0]
      return {
        id: col.id,
        elementIds: rows.map((item) => item?.id).filter(Boolean),
        rowCount: rows.length,
      };
    });
  }

  return {
    isInCardLogic,
    currentSelection: {
      headerCellNodeElementId,
      cellNodeElementId,
    },
    columnCount,
    colInfo,
  };
}

function toggleH3EnabledAtSelection({ editor }) {
  const { selection } = editor;
  if (selection) {
    const [headerNodes] = Editor.nodes(editor, {
      at: selection,
      match: (node: any) => node?.type === CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE,
    });
    if (headerNodes) {
      const [node, path] = headerNodes as any;
      Transforms.setNodes(editor, { h3Enabled: !node.h3Enabled }, { at: path });
    }
  }
}

export const CardLogicTransformActions = {
  addNewCardLogic,
  setNodesByElementId,
  insertCardByElementId,
  insertColummByElementId,
  retrieveTableInfoByElementId,
  deleteCardByElementId,
  removeColumnByElementId,
  toggleH3EnabledAtSelection,
};
